import React from 'react'
import { Autocomplete, Box, Checkbox, TextField, Typography } from '@mui/material'
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined'
import { ApiPushGetGroups, ApiPushLocations } from '../../apiService/Core/Apicall'
import PushPanel from './PushPanel'
import { InlineAlert } from './PushDisabledNotice'
import UserPicker from './UserPicker'
import { checkboxSx, formFieldSx, menuPaperSx, subLabelSx } from './pushStyles'
import { getErrorMessage, MANUAL_USERS_MAX, qs, regionName, SEGMENT_OPTIONS } from './pushUtils'
import type { AudienceState, Segment } from './types'

interface Props {
    value: AudienceState
    onChange: (next: AudienceState) => void
}

interface GroupOpt { id: string; name: string; memberCount: number }
interface CountryOpt { code: string; count: number; states: { name: string; count: number }[] }

const acPaper = { slotProps: { paper: { sx: menuPaperSx } } }
const chipSx = { background: '#241815', color: '#e0e0e0', border: '1px solid #ff6a3d40', '& .MuiChip-deleteIcon': { color: '#ff6a3d' } }
const acSlotProps = { ...acPaper.slotProps, chip: { sx: chipSx }, popupIndicator: { sx: { color: '#898989' } }, clearIndicator: { sx: { color: '#898989' } } }

const AudienceBuilder: React.FC<Props> = ({ value, onChange }) => {
    const [groups, setGroups] = React.useState<GroupOpt[]>([])
    const [countries, setCountries] = React.useState<CountryOpt[]>([])
    const [groupsLoading, setGroupsLoading] = React.useState(true)
    const [locLoading, setLocLoading] = React.useState(true)
    const [error, setError] = React.useState<string | null>(null)

    React.useEffect(() => {
        let cancelled = false
        ApiPushGetGroups(qs({ page: 1, limit: 100 }), { silent: true })
            .then((res) => { if (!cancelled) setGroups(res?.data?.items ?? []) })
            .catch((e) => { if (!cancelled) setError(getErrorMessage(e)) })
            .finally(() => { if (!cancelled) setGroupsLoading(false) })
        ApiPushLocations()
            .then((res) => { if (!cancelled) setCountries(res?.data?.countries ?? []) })
            .catch((e) => { if (!cancelled) setError(getErrorMessage(e)) })
            .finally(() => { if (!cancelled) setLocLoading(false) })
        return () => { cancelled = true }
    }, [])

    const stateOptions = countries
        .filter((c) => value.countries.includes(c.code))
        .flatMap((c) => (c.states ?? []).map((s) => ({ country: c.code, state: s.name, count: s.count })))

    const patch = (p: Partial<AudienceState>) => onChange({ ...value, ...p })
    const toggleSegment = (s: Segment) =>
        patch({ segments: value.segments.includes(s) ? value.segments.filter((x) => x !== s) : [...value.segments, s] })

    const checkRow = (checked: boolean, onToggle: () => void, label: string, help?: string) => (
        <Box
            onClick={onToggle}
            sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, cursor: 'pointer', py: 0.3, borderRadius: '8px', '&:hover': { background: '#ff6a3d08' } }}
        >
            <Checkbox checked={checked} sx={checkboxSx} disableRipple tabIndex={-1} />
            <Box sx={{ pt: 0.6 }}>
                <Typography sx={{ fontSize: 13, color: '#e0e0e0' }}>{label}</Typography>
                {help && <Typography sx={{ fontSize: 11, color: '#6a6a6a' }}>{help}</Typography>}
            </Box>
        </Box>
    )

    return (
        <PushPanel title="Audience" icon={<GroupsOutlinedIcon sx={{ color: '#ff6a3d', fontSize: 20 }} />}>
            <InlineAlert severity="info" sx={{ mb: 2 }}>
                All selected sources are combined (union), duplicates removed.
            </InlineAlert>
            {error && <InlineAlert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</InlineAlert>}

            {checkRow(value.includeAll, () => patch({ includeAll: !value.includeAll }), 'All eligible users')}

            <Typography sx={{ ...subLabelSx, mt: 2 }}>User segments</Typography>
            {SEGMENT_OPTIONS.map((s) => (
                <React.Fragment key={s.key}>
                    {checkRow(value.segments.includes(s.key as Segment), () => toggleSegment(s.key as Segment), s.label, s.help)}
                </React.Fragment>
            ))}

            <Typography sx={{ ...subLabelSx, mt: 2 }}>Groups</Typography>
            <Autocomplete
                multiple
                size="small"
                loading={groupsLoading}
                options={groups}
                value={groups.filter((g) => value.groupIds.includes(g.id))}
                getOptionLabel={(g) => `${g.name} (${g.memberCount})`}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                onChange={(_, v) => patch({ groupIds: v.map((g) => g.id) })}
                renderInput={(params) => <TextField {...params} placeholder="Select groups" sx={formFieldSx} />}
                noOptionsText="No groups"
                slotProps={acSlotProps}
            />

            <Typography sx={{ ...subLabelSx, mt: 2 }}>Location (from stored profile / address data)</Typography>
            <Autocomplete
                multiple
                size="small"
                loading={locLoading}
                options={countries}
                value={countries.filter((c) => value.countries.includes(c.code))}
                getOptionLabel={(c) => `${regionName(c.code)} (${c.count})`}
                isOptionEqualToValue={(a, b) => a.code === b.code}
                onChange={(_, v) => {
                    const codes = v.map((c) => c.code)
                    patch({ countries: codes, states: value.states.filter((s) => codes.includes(s.country)) })
                }}
                renderInput={(params) => <TextField {...params} placeholder="Select countries" sx={formFieldSx} />}
                noOptionsText="No countries"
                slotProps={acSlotProps}
            />
            <Autocomplete
                multiple
                size="small"
                sx={{ mt: 1.5 }}
                disabled={value.countries.length === 0}
                options={stateOptions}
                groupBy={(o) => regionName(o.country)}
                value={stateOptions.filter((o) => value.states.some((s) => s.country === o.country && s.state === o.state))}
                getOptionLabel={(o) => `${o.state} (${o.count})`}
                isOptionEqualToValue={(a, b) => a.country === b.country && a.state === b.state}
                onChange={(_, v) => patch({ states: v.map((o) => ({ country: o.country, state: o.state })) })}
                renderInput={(params) => (
                    <TextField {...params} placeholder={value.countries.length ? 'Select states' : 'Select a country first'} sx={formFieldSx} />
                )}
                noOptionsText="No states"
                slotProps={acSlotProps}
            />

            <Typography sx={{ ...subLabelSx, mt: 2 }}>Individual users</Typography>
            <UserPicker selected={value.users} onChange={(users) => patch({ users })} max={MANUAL_USERS_MAX} />
        </PushPanel>
    )
}

export default AudienceBuilder
